<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useGameStore } from '@/stores/game';
import AttributionOverlay from '@/components/AttributionOverlay.vue'

/**
 * 画像を表示するための専門コンポーネント。
 */

defineProps<{
    /**
     * 表示する画像のURL
     */
    imageUrl: string | null
}>()

const { t } = useI18n();
const gameStore = useGameStore();

// クイズ画像の場合、ゲーム中(未回答)はズームアウトのアニメーションを適用
// Backend handles image processing, so client-side scaling is not needed.
// Keeping this clean for any future client-side effects.

// 解像度ゲームなどの場合のスタイル
const imageFilter = computed(() => {
    return {};
});

// 結果表示時は画像を枠に収める（全体を表示するため）
const imageStyle = computed(() => {
    // answerResultがある＝正解発表時（正解画像）
    // Game is finished = 終了時
    if (gameStore.answerResult || gameStore.isGameFinished) {
        return { objectFit: 'contain' as const };
    }
    return { objectFit: 'cover' as const };
});
</script>

<template>
    <div class="image-display-container">
        <div class="image-wrapper">
            <!-- Countdown Overlay -->
            <div v-if="gameStore.isCountdownActive" class="countdown-overlay">
                <span class="countdown-number">{{ gameStore.countdownValue }}</span>
            </div>

            <!-- Hide image during countdown -->
            <img v-show="!gameStore.isCountdownActive && imageUrl" :src="imageUrl" :alt="t('game.image_alt')"
                class="zoom-image" :style="[imageFilter, imageStyle]" />

            <!-- オーバーレイを追加: 回答結果が表示されたタイミング、またはゲーム終了時のみ表示（カンニング防止＆マルチ同期） -->
            <!-- 修正: isAnswered (個人の回答完了) ではなく answerResult (サーバーからの正解発表) をトリガーにする -->
            <AttributionOverlay
                v-if="gameStore.currentQuestion?.attribution && (gameStore.answerResult || gameStore.isGameFinished)"
                :attribution="gameStore.currentQuestion.attribution" />
        </div>
    </div>
</template>

<style scoped>
.image-display-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 5px;
    /* 3px -> 5px */
}

.image-wrapper {
    width: 320px;
    /* 350px -> 320px */
    height: 320px;
    /* 350px -> 320px */
    max-width: 85vw;
    max-height: 85vw;
    overflow: hidden;
    border: 3px solid #ddd;
    border-radius: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    position: relative;
    background-color: #f0f0f0;
    /* contain時の余白用背景色 */
    /* オーバーレイの基準位置 */
}

@media (min-width: 600px) {
    .image-wrapper {
        width: 480px;
        height: 480px;
    }
}

@media (min-width: 1024px) {
    .image-wrapper {
        width: 500px;
        height: 500px;
    }
}

.zoom-image {
    width: 100%;
    height: 100%;
    /* object-fit is now handled by inline style */
    transform-origin: center center;
}

.countdown-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f0f0f0;
    z-index: 10;
}

.countdown-number {
    font-size: 8rem;
    font-weight: bold;
    color: #333;
    animation: pulse 1s infinite;
}

@keyframes pulse {
    0% {
        transform: scale(1);
        opacity: 1;
    }

    50% {
        transform: scale(1.2);
        opacity: 0.8;
    }

    100% {
        transform: scale(1);
        opacity: 1;
    }
}
</style>
